import React, { useState, useRef, useCallback } from "react";

import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";

const Tooltip = ({
  children,
  text,
  position = "right",
  horizontalAlign = "center",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [finalPosition, setFinalPosition] = useState(position);
  const ref = useRef(null);
  const tooltipRef = useRef(null);

  const updateTooltipPosition = useCallback(
    (e) => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 16;
      const offset = 12; // Distance from cursor

      // Get actual tooltip dimensions if available
      let tooltipWidth = Math.min(400, viewportWidth - padding * 2);
      let tooltipHeight = 60;

      if (tooltipRef.current) {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();

        tooltipWidth = Math.min(
          Math.max(tooltipRect.width, 200),
          viewportWidth - padding * 2
        );
        tooltipHeight = Math.max(tooltipRect.height, 40);
      }

      const triggerRect = ref.current?.getBoundingClientRect();
      let anchorX = e.clientX;
      let y = e.clientY;

      if (horizontalAlign === "right" && triggerRect) {
        anchorX = triggerRect.right;
      } else if (horizontalAlign === "left" && triggerRect) {
        anchorX = triggerRect.left;
      }

      // Determine best position based on cursor location
      const spaces = {
        top: y,
        bottom: viewportHeight - y,
        left: anchorX,
        right: viewportWidth - anchorX,
      };

      // Default to showing above cursor
      let bestPosition = "top";

      // Choose best position based on available space
      if (spaces.top > tooltipHeight + padding) {
        bestPosition = "top";
        y = y - tooltipHeight - offset;
      } else if (spaces.bottom > tooltipHeight + padding) {
        bestPosition = "bottom";
        y = y + offset;
      } else {
        bestPosition = "top";
        y = y - tooltipHeight - offset;
      }

      setFinalPosition(bestPosition);

      let x = anchorX - tooltipWidth / 2;

      if (horizontalAlign === "right") {
        x = anchorX - tooltipWidth;
      } else if (horizontalAlign === "left") {
        x = anchorX;
      }

      // Ensure tooltip stays within viewport horizontally.
      const maxX = Math.max(padding, viewportWidth - tooltipWidth - padding);

      if (x < padding) {
        x = padding;
      } else if (x > maxX) {
        x = maxX;
      }

      // Ensure tooltip stays within viewport vertically
      if (y < padding) {
        y = padding;
      } else if (y + tooltipHeight > viewportHeight - padding) {
        y = viewportHeight - tooltipHeight - padding;
      }

      setCoords({ x, y });
    },
    [horizontalAlign]
  );

  const tooltipAlignmentClass =
    horizontalAlign === "right"
      ? "items-end"
      : horizontalAlign === "left"
        ? "items-start"
        : "items-center";

  return (
    <>
      <div
        className="relative inline-flex"
        ref={ref}
        onMouseEnter={(event) => {
          updateTooltipPosition(event);
          setIsVisible(true);
        }}
        onMouseLeave={() => setIsVisible(false)}
        onMouseMove={updateTooltipPosition}
      >
        {children}
      </div>
      {isVisible &&
        text &&
        createPortal(
          <AnimatePresence>
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="fixed pointer-events-none"
              exit={{
                opacity: 0,
                y:
                  finalPosition === "bottom"
                    ? -4
                    : finalPosition === "top"
                      ? 4
                      : 0,
              }}
              initial={{
                opacity: 0,
                y:
                  finalPosition === "bottom"
                    ? -4
                    : finalPosition === "top"
                      ? 4
                      : 0,
              }}
              key="tooltip"
              ref={tooltipRef}
              style={{
                zIndex: 99999,
                left: `${coords.x}px`,
                top: `${coords.y}px`,
              }}
              transition={{ duration: 0.15 }}
            >
              <div
                className={`relative flex flex-col ${tooltipAlignmentClass}`}
              >
                {finalPosition === "bottom" && (
                  <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-b-[5px] border-b-gray-900 -mb-[1px]" />
                )}
                <div
                  className="bg-gray-900 text-white text-xs px-3 py-2 rounded-md shadow-xl font-medium break-words whitespace-normal"
                  style={{ maxWidth: "min(24rem, calc(100vw - 2rem))" }}
                >
                  {text}
                </div>
                {finalPosition === "top" && (
                  <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-gray-900 -mt-[1px]" />
                )}
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
